import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useEffect, useRef, useState } from "react";
import "./index.css";
import { useLanguage } from "contexts/language.context";
import { Add, Delete, Edit } from "@mui/icons-material";
import LoadingButton from "../LoadingButton";

export default function DropdownSelect({
  items,
  initialValue,
  onChange,
  label,
  required,
  multiple,
  hasAll,
  renderOption,
  loading,
  defaultValue,
  crud,
  inputProps,
  ...rest
}) {
  const language = useLanguage();

  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(multiple ? [] : null);
  const [firstRender, setFirstRender] = useState(true);
  const [open, setOpen] = useState({});
  const [loader, setLoader] = useState({});
  const autocompeleteRef = useRef();

  const filterOptions = createFilterOptions({
    stringify: (option) =>
      (rest.groupBy ? rest.groupBy(option) + " " : "") + option.label,
  });

  useEffect(() => {
    if (!items) setOptions([]);
    else {
      setOptions(
        hasAll && multiple
          ? [{ id: -1, label: language.label("all") }, ...items]
          : items,
      );
    }
  }, [items]);

  useEffect(() => {
    setFirstRender(false);
    if (items) {
      if (multiple) {
        setValue(
          items.filter((i) =>
            (firstRender && !initialValue
              ? defaultValue
              : initialValue
            )?.includes?.(i?.id),
          ),
        );
      } else {
        setValue(
          items.find(
            (i) =>
              i.id ==
              (firstRender && !initialValue ? defaultValue : initialValue),
          ) || null,
        );
      }
    } else {
      setValue(null);
    }
  }, [initialValue, items]);

  return (
    <>
      <Autocomplete
        ref={autocompeleteRef}
        size="medium"
        value={value}
        onChange={(event, newValue) => {
          if (multiple) {
            const allPresentNow = newValue.findIndex((v) => v.id === -1) !== -1;
            const allPresentBefore = value.findIndex((v) => v.id === -1) !== -1;
            if (allPresentNow && !allPresentBefore) {
              setValue(options);
              onChange && onChange(options);
              return;
            } else if (allPresentNow && allPresentBefore) {
              const filteredOptions = newValue.filter((v) => v.id !== -1);
              setValue(filteredOptions);
              onChange && onChange(filteredOptions);
              return;
            } else if (!allPresentNow && allPresentBefore) {
              setValue([]);
              onChange && onChange([]);
              return;
            }
          }

          setValue(newValue);
          onChange && onChange(newValue);
        }}
        filterOptions={filterOptions}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }
          return option?.label || "";
        }}
        getOptionKey={(option) => {
          if (typeof option === "string") {
            return option;
          }
          return option?.id;
        }}
        isOptionEqualToValue={(option, value) => {
          return (
            option?.id == value?.id || option?.id == value || option == value
          );
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <MenuItem
              component="li"
              key={key}
              {...optionProps}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "100%",
                ...(optionProps?.sx || {}),
              }}
            >
              {renderOption ? (
                renderOption(option)
              ) : (
                <Typography sx={{ flex: 1 }} variant="inherit">
                  {option?.label}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 0,
                  alignItems: "center",
                  justifyContent: "center",
                  mr: -2,
                }}
              >
                {crud?.update ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen({
                        ...open,
                        manage: {
                          mode: "update",
                          ...option,
                        },
                      });
                    }}
                  >
                    <Edit />
                  </IconButton>
                ) : null}

                {crud?.remove ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen({
                        ...open,
                        manage: {
                          mode: "remove",
                          ...option,
                        },
                      });
                    }}
                  >
                    <Delete />
                  </IconButton>
                ) : null}
              </Box>
            </MenuItem>
          );
        }}
        freeSolo
        forcePopupIcon={true}
        renderInput={(params) => {
          return (
            <>
              <TextField
                {...params}
                label={label}
                required={required}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: crud?.create ? (
                    <>
                      {params.InputProps?.endAdornment}
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpen((open) => ({
                              ...open,
                              manage: {
                                mode: "create",
                              },
                            }));
                          }}
                        >
                          <Add />
                        </IconButton>
                      </InputAdornment>
                    </>
                  ) : (
                    params.InputProps?.endAdornment
                  ),
                  ...rest?.InputProps,
                }}
                InputLabelProps={{
                  ...rest?.InputLabelProps,
                }}
                FormHelperTextProps={{
                  sx: { fontWeight: "400" },
                }}
                {...inputProps}
              />
              {loading && (
                <CircularProgress
                  sx={{ position: "absolute", right: 13, top: 13 }}
                  size={20}
                  color="info"
                  thickness={4}
                />
              )}
            </>
          );
        }}
        readOnly={loading || rest?.readOnly}
        renderTags={(value, getTagProps) => {
          const allIndex = value.findIndex((v) => v.id === -1);
          return (
            <Grid container sx={{ padding: 1 }}>
              {allIndex !== -1 ? (
                <Grid item key={getTagProps({ allIndex }).key}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={language.label("all")}
                    {...getTagProps({ allIndex })}
                  />
                </Grid>
              ) : (
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Grid key={key} item>
                      <Chip
                        variant="outlined"
                        label={option.label}
                        {...tagProps}
                      />
                    </Grid>
                  );
                })
              )}
            </Grid>
          );
        }}
        multiple={multiple}
        sx={{
          position: "relative",
          ...rest?.sx,
        }}
        slotProps={{
          clearIndicator: {
            sx: {
              visibility: "visible !important",
              opacity: "1 !important",
            },
            size: "small",
          },
          ...(rest?.slotProps || {}),
        }}
        {...rest}
      />
      {crud && open.manage ? (
        <Popover
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          anchorEl={autocompeleteRef.current}
          open={!!open.manage}
          onClose={() => {
            setOpen((open) => ({ ...open, manage: false }));
          }}
          component="form"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const formData = new FormData(e.currentTarget);
            const formValues = Object.fromEntries(formData.entries());
            setLoader((loader) => ({ ...loader, submit: true }));
            let ok;
            if (open.manage.mode == "update") {
              ok = await crud.update(open.manage.id, formValues);
            } else if (open.manage.mode == "create") {
              ok = await crud.create(formValues);
            } else if (open.manage.mode == "remove") {
              ok = await crud.remove(open.manage.id);

              if (value?.id == open.manage.id) {
                setValue(null);
              } else if (value?.find?.((v) => v.id == open.manage.id))
                setValue((v) => v.filter((t) => t.id != open.manage.id));
            }

            if (ok) {
              await crud.refresh();
              setOpen((open) => ({ ...open, manage: false }));
            }

            setLoader((loader) => ({ ...loader, submit: false }));
          }}
          sx={{ p: 1 }}
          slotProps={{
            paper: {
              variant: "outlined",
              sx: {
                p: 1,
                overflow: "visible",
                backgroundColor: "white.main",
                mt: "2px",
                "&::before": {
                  borderTop: "1px solid",
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  left: "24px",
                  width: 12,
                  height: 12,
                  bgcolor: "background.paper",
                  transform: "translate(-50%, -50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            },
          }}
        >
          {open.manage.mode == "remove" ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                alignItems: "end",
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                Are you sure you want to delete "{open.manage.label}"?
              </Typography>

              <LoadingButton
                loading={loader?.submit}
                type="submit"
                variant="gradient"
                color="error"
                size="small"
              >
                Delete
              </LoadingButton>
            </Box>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {open.manage.mode == "create" ? "Create" : "Edit"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <TextField
                  name="label"
                  size="small"
                  defaultValue={open.manage.label || ""}
                />

                <LoadingButton
                  loading={loader?.submit}
                  type="submit"
                  variant="gradient"
                  color="info"
                  size="small"
                >
                  {open.manage.mode == "create" ? "Create" : "Save"}
                </LoadingButton>
              </Box>
            </>
          )}
        </Popover>
      ) : null}
    </>
  );
}
